// ── Wallet core: mnemonic, per-chain key derivation, encrypted vault ─────────────
// Runs entirely in the browser. The mnemonic and private keys never leave this module
// except to be handed to a chain's own signing function (also client-side) — nothing here
// ever does a network call.
import * as bip39 from 'bip39';
import { HDNodeWallet, Mnemonic, keccak256, getBytes } from 'ethers';
import { BIP32Factory } from 'bip32';
import * as ecc from '@bitcoinerlab/secp256k1';
import * as bitcoin from 'bitcoinjs-lib';
import { derivePath as edDerivePath } from 'ed25519-hd-key';
import { Keypair } from '@solana/web3.js';
import bs58 from 'bs58';
import { sha256 } from '@noble/hashes/sha256';

const bip32 = BIP32Factory(ecc);
bitcoin.initEccLib(ecc);

export function generateMnemonic(strength = 128) {
  return bip39.generateMnemonic(strength); // 128 bits = 12 words
}

export function validateMnemonic(phrase) {
  return bip39.validateMnemonic(normalizePhrase(phrase));
}

export function normalizePhrase(phrase) {
  return (phrase || '').trim().split(/\s+/).join(' ').toLowerCase();
}

function base58checkEncode(payload) {
  const checksum = sha256(sha256(payload)).slice(0, 4);
  const full = new Uint8Array(payload.length + 4);
  full.set(payload); full.set(checksum, payload.length);
  return bs58.encode(full);
}

function deriveEvm(mnemonic, index) {
  const w = HDNodeWallet.fromMnemonic(Mnemonic.fromPhrase(mnemonic), `m/44'/60'/0'/0/${index}`);
  return { address: w.address, privateKey: w.privateKey };
}

function deriveBitcoin(mnemonic, index) {
  const seed = bip39.mnemonicToSeedSync(mnemonic);
  const root = bip32.fromSeed(seed);
  const child = root.derivePath(`m/84'/0'/0'/0/${index}`);
  const { address } = bitcoin.payments.p2wpkh({ pubkey: Buffer.from(child.publicKey), network: bitcoin.networks.bitcoin });
  return { address, privateKeyWIF: child.toWIF(), publicKey: Buffer.from(child.publicKey).toString('hex') };
}

function deriveSolana(mnemonic, index) {
  const seed = bip39.mnemonicToSeedSync(mnemonic);
  const { key } = edDerivePath(`m/44'/501'/${index}'/0'`, seed.toString('hex'));
  const kp = Keypair.fromSeed(Uint8Array.from(key.slice(0, 32)));
  return { address: kp.publicKey.toBase58(), secretKey: bs58.encode(kp.secretKey) };
}

function deriveTron(mnemonic, index) {
  const seed = bip39.mnemonicToSeedSync(mnemonic);
  const root = bip32.fromSeed(seed);
  const child = root.derivePath(`m/44'/195'/0'/0/${index}`);
  const pub = ecc.pointFromScalar(child.privateKey, false); // uncompressed, 65 bytes (0x04 prefix)
  const hash = keccak256(getBytes(pub.slice(1))); // drop 0x04 prefix, keccak the 64-byte point
  const addressBytes = new Uint8Array(21);
  addressBytes[0] = 0x41;
  addressBytes.set(getBytes(hash).slice(-20), 1);
  return { address: base58checkEncode(addressBytes), privateKey: Buffer.from(child.privateKey).toString('hex') };
}

// Derive every chain's keypair for one account index. EVM chains share a single
// address/key (exactly like every real wallet — one EVM account, many EVM networks).
export function deriveAccount(mnemonic, index = 0) {
  const evm = deriveEvm(mnemonic, index);
  return {
    index,
    evm,                              // { address, privateKey } — used for Ethereum/BNB/Polygon/Arbitrum/Optimism/Base/Avalanche
    bitcoin: deriveBitcoin(mnemonic, index),
    solana: deriveSolana(mnemonic, index),
    tron: deriveTron(mnemonic, index),
  };
}

// ── Encrypted vault (AES-GCM, PBKDF2-derived key) ────────────────────────────────
// Stored shape in localStorage: { salt, iv, iterations, ciphertext } — all base64.
// The mnemonic is the only secret ever encrypted; everything else (addresses, labels,
// account count) is derivable/public and can stay in plain localStorage.
const PBKDF2_ITERATIONS = 210000;

function b64(buf) { return btoa(String.fromCharCode(...new Uint8Array(buf))); }
function unb64(str) { return Uint8Array.from(atob(str), c => c.charCodeAt(0)); }

async function deriveKey(password, salt) {
  const keyMaterial = await crypto.subtle.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    keyMaterial, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']
  );
}

export async function encryptVault(password, plaintextObj) {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(password, salt);
  const data = new TextEncoder().encode(JSON.stringify(plaintextObj));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);
  return { v: 1, salt: b64(salt), iv: b64(iv), iterations: PBKDF2_ITERATIONS, ciphertext: b64(ciphertext) };
}

export async function decryptVault(password, vault) {
  const salt = unb64(vault.salt);
  const iv = unb64(vault.iv);
  const key = await deriveKey(password, salt);
  try {
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, unb64(vault.ciphertext));
    return JSON.parse(new TextDecoder().decode(plain));
  } catch {
    throw new Error('Incorrect password.');
  }
}
